import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Heart, Users, Trash2, Eye, Check, X } from 'lucide-react';
import './AdminSubmissions.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSubmissions = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [donations, setDonations] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [counts, setCounts] = useState({ contacts: {}, donations: {}, memberships: {} });
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [contactsRes, donationsRes, membershipsRes, countsRes] = await Promise.all([
        axios.get(`${API}/submissions/contacts`, { headers }),
        axios.get(`${API}/submissions/donations`, { headers }),
        axios.get(`${API}/submissions/memberships`, { headers }),
        axios.get(`${API}/submissions/counts`, { headers })
      ]);
      
      setContacts(contactsRes.data);
      setDonations(donationsRes.data);
      setMemberships(membershipsRes.data);
      setCounts(countsRes.data);
    } catch (error) {
      console.error('Erro ao carregar submissões:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (type, id, status) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/submissions/${type}/${id}/status?status=${status}`, {}, { headers });
      // Update selected item's status locally
      if (selectedItem && selectedItem.id === id) {
        setSelectedItem(prev => ({ ...prev, status }));
      }
      loadData();
    } catch (error) {
      console.error('Erro ao atualizar estado:', error);
    }
  };

  const deleteSubmission = async (type, id) => {
    if (!window.confirm('Tem certeza que deseja eliminar esta submissão?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/submissions/${type}/${id}`, { headers });
      loadData();
      setSelectedItem(null);
    } catch (error) {
      console.error('Erro ao eliminar:', error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      novo: '#2196f3',
      lido: '#ff9800',
      respondido: '#4caf50',
      pendente: '#ff9800',
      pago: '#4caf50',
      cancelado: '#f44336',
      aprovado: '#4caf50',
      rejeitado: '#f44336'
    };
    
    return (
      <span className="status-badge" style={{ background: statusColors[status] || '#999' }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return <div className="loading">A carregar...</div>;
  }

  return (
    <div className="admin-submissions">
      <div className="submissions-header">
        <h1>Submissões</h1>
        <p>Gestão de formulários recebidos</p>
      </div>

      <div className="tabs-nav">
        <button 
          className={`tab-btn ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          <Mail size={18} />
          Contactos
          {counts.contacts?.new > 0 && (
            <span className="badge">{counts.contacts.new}</span>
          )}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'donations' ? 'active' : ''}`}
          onClick={() => setActiveTab('donations')}
        >
          <Heart size={18} />
          Donativos
          {counts.donations?.pending > 0 && (
            <span className="badge">{counts.donations.pending}</span>
          )}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'memberships' ? 'active' : ''}`}
          onClick={() => setActiveTab('memberships')}
        >
          <Users size={18} />
          Novos Sócios
          {counts.memberships?.pending > 0 && (
            <span className="badge">{counts.memberships.pending}</span>
          )}
        </button>
      </div>

      <div className="submissions-content">
        {/* CONTACTS TAB */}
        {activeTab === 'contacts' && (
          <div className="submissions-list">
            {contacts.length === 0 ? (
              <div className="empty-state">
                <Mail size={48} color="#ccc" />
                <p>Ainda não existem mensagens de contacto</p>
              </div>
            ) : (
              contacts.map(contact => (
                <div 
                  key={contact.id} 
                  className={`submission-card ${selectedItem?.id === contact.id ? 'selected' : ''}`}
                  onClick={() => setSelectedItem({ ...contact, type: 'contacts' })}
                >
                  <div className="card-header">
                    <strong>{contact.nome}</strong>
                    {getStatusBadge(contact.status)}
                  </div>
                  <div className="card-subject">{contact.assunto}</div>
                  <div className="card-meta">
                    <span>{contact.email}</span>
                    <span>{formatDate(contact.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* DONATIONS TAB */}
        {activeTab === 'donations' && (
          <div className="submissions-list">
            {donations.length === 0 ? (
              <div className="empty-state">
                <Heart size={48} color="#ccc" />
                <p>Ainda não existem pedidos de donativo</p>
              </div>
            ) : (
              donations.map(donation => (
                <div 
                  key={donation.id} 
                  className={`submission-card ${selectedItem?.id === donation.id ? 'selected' : ''}`}
                  onClick={() => setSelectedItem({ ...donation, type: 'donations' })}
                >
                  <div className="card-header">
                    <strong>{donation.nome}</strong>
                    {getStatusBadge(donation.status)}
                  </div>
                  <div className="card-value">{donation.valor}€ via {donation.metodo_pagamento}</div>
                  <div className="card-meta">
                    <span>{donation.email}</span>
                    <span>{formatDate(donation.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* MEMBERSHIPS TAB */}
        {activeTab === 'memberships' && (
          <div className="submissions-list">
            {memberships.length === 0 ? (
              <div className="empty-state">
                <Users size={48} color="#ccc" />
                <p>Ainda não existem pedidos de associação</p>
              </div>
            ) : (
              memberships.map(member => (
                <div 
                  key={member.id} 
                  className={`submission-card ${selectedItem?.id === member.id ? 'selected' : ''}`}
                  onClick={() => setSelectedItem({ ...member, type: 'memberships' })}
                >
                  <div className="card-header">
                    <strong>{member.nome_completo}</strong>
                    {getStatusBadge(member.status)}
                  </div>
                  <div className="card-subject">{member.email}</div>
                  <div className="card-meta">
                    <span>{member.localidade}</span>
                    <span>{formatDate(member.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* DETAIL PANEL */}
        {selectedItem && (
          <div className="detail-panel">
            <div className="panel-header">
              <h3>Detalhes</h3>
              <button className="close-btn" onClick={() => setSelectedItem(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="panel-content">
              {selectedItem.type === 'contacts' && (
                <>
                  <div className="detail-row">
                    <label>Nome:</label>
                    <span>{selectedItem.nome}</span>
                  </div>
                  <div className="detail-row">
                    <label>Email:</label>
                    <span>{selectedItem.email}</span>
                  </div>
                  <div className="detail-row">
                    <label>Assunto:</label>
                    <span>{selectedItem.assunto}</span>
                  </div>
                  <div className="detail-row">
                    <label>Mensagem:</label>
                    <p className="message-text">{selectedItem.mensagem}</p>
                  </div>
                  <div className="detail-row">
                    <label>Data:</label>
                    <span>{formatDate(selectedItem.created_at)}</span>
                  </div>
                  <div className="detail-actions">
                    <select 
                      value={selectedItem.status}
                      onChange={(e) => updateStatus('contacts', selectedItem.id, e.target.value)}
                    >
                      <option value="novo">Novo</option>
                      <option value="lido">Lido</option>
                      <option value="respondido">Respondido</option>
                    </select>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteSubmission('contacts', selectedItem.id)}
                    >
                      <Trash2 size={16} /> Eliminar
                    </button>
                  </div>
                </>
              )}

              {selectedItem.type === 'donations' && (
                <>
                  <div className="detail-row">
                    <label>Nome:</label>
                    <span>{selectedItem.nome}</span>
                  </div>
                  <div className="detail-row">
                    <label>Email:</label>
                    <span>{selectedItem.email}</span>
                  </div>
                  <div className="detail-row">
                    <label>Telefone:</label>
                    <span>{selectedItem.telefone}</span>
                  </div>
                  <div className="detail-row highlight">
                    <label>Valor:</label>
                    <span className="value-highlight">{selectedItem.valor}€</span>
                  </div>
                  <div className="detail-row">
                    <label>Método:</label>
                    <span>{selectedItem.metodo_pagamento}</span>
                  </div>
                  <div className="detail-row">
                    <label>Visibilidade:</label>
                    <span>{selectedItem.visibilidade}</span>
                  </div>
                  <div className="detail-row">
                    <label>Recibo:</label>
                    <span>{selectedItem.recibo ? 'Sim' : 'Não'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Data:</label>
                    <span>{formatDate(selectedItem.created_at)}</span>
                  </div>
                  <div className="detail-actions">
                    <select 
                      value={selectedItem.status}
                      onChange={(e) => updateStatus('donations', selectedItem.id, e.target.value)}
                    >
                      <option value="pendente">Pendente</option>
                      <option value="pago">Pago</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteSubmission('donations', selectedItem.id)}
                    >
                      <Trash2 size={16} /> Eliminar
                    </button>
                  </div>
                </>
              )}

              {selectedItem.type === 'memberships' && (
                <>
                  <div className="detail-row">
                    <label>Nome Completo:</label>
                    <span>{selectedItem.nome_completo}</span>
                  </div>
                  <div className="detail-row">
                    <label>Data Nascimento:</label>
                    <span>{selectedItem.data_nascimento}</span>
                  </div>
                  <div className="detail-row">
                    <label>Naturalidade:</label>
                    <span>{selectedItem.naturalidade}</span>
                  </div>
                  <div className="detail-row">
                    <label>Nacionalidade:</label>
                    <span>{selectedItem.nacionalidade}</span>
                  </div>
                  <div className="detail-row">
                    <label>Estado Civil:</label>
                    <span>{selectedItem.estado_civil}</span>
                  </div>
                  <div className="detail-row">
                    <label>Profissão:</label>
                    <span>{selectedItem.profissao}</span>
                  </div>
                  <div className="detail-row">
                    <label>Morada:</label>
                    <span>{selectedItem.morada}</span>
                  </div>
                  <div className="detail-row">
                    <label>Código Postal:</label>
                    <span>{selectedItem.codigo_postal}</span>
                  </div>
                  <div className="detail-row">
                    <label>Localidade:</label>
                    <span>{selectedItem.localidade}</span>
                  </div>
                  <div className="detail-row">
                    <label>Telefone:</label>
                    <span>{selectedItem.telefone}</span>
                  </div>
                  <div className="detail-row">
                    <label>Telemóvel:</label>
                    <span>{selectedItem.telemovel}</span>
                  </div>
                  <div className="detail-row">
                    <label>Email:</label>
                    <span>{selectedItem.email}</span>
                  </div>
                  <div className="detail-row">
                    <label>NIF:</label>
                    <span>{selectedItem.nif}</span>
                  </div>
                  <div className="detail-row">
                    <label>Cartão Cidadão:</label>
                    <span>{selectedItem.cartao_cidadao}</span>
                  </div>
                  <div className="detail-row">
                    <label>IBAN:</label>
                    <span>{selectedItem.iban}</span>
                  </div>
                  <div className="detail-row">
                    <label>Data:</label>
                    <span>{formatDate(selectedItem.created_at)}</span>
                  </div>
                  <div className="detail-actions">
                    <select 
                      value={selectedItem.status}
                      onChange={(e) => updateStatus('memberships', selectedItem.id, e.target.value)}
                    >
                      <option value="pendente">Pendente</option>
                      <option value="aprovado">Aprovado</option>
                      <option value="rejeitado">Rejeitado</option>
                    </select>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteSubmission('memberships', selectedItem.id)}
                    >
                      <Trash2 size={16} /> Eliminar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubmissions;
